import { useLanguageContext } from "@/context/LanguageContext";
import { usePrice } from "@/context/PriceContext";
import { useWatchlist } from "@/context/WatchlistContext";
import { useEffect } from "react";

export const EnhanceTokenLinks = () => {
  const { language } = useLanguageContext();
  const { getToken } = usePrice(); // Use the PriceContext to get token data
  const { handleAddTokenToWatchlist } = useWatchlist();

  useEffect(() => {
    // Skip on server
    if (typeof window === "undefined") return;

    // Create a popover element that will be shown on hover
    const createPopover = (symbol: string) => {
      const popover = document.createElement("div");
      popover.className = "token-popover";
      popover.setAttribute("data-token-symbol", symbol); 
      popover.style.position = "absolute";
      popover.style.zIndex = "1000";
      popover.style.backgroundColor = "white";
      popover.style.border = "1px solid #f0f0f0";
      popover.style.borderRadius = "8px";
      popover.style.boxShadow = "0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08)";
      popover.style.padding = "12px";
      popover.style.minWidth = "250px";
      popover.style.display = "none";
      popover.style.opacity = "0";
      popover.style.transition = "opacity 0.2s";

      // Initial loading state
      popover.innerHTML = `
        <div class="token-popover-loading">
          <div style="height: 20px; width: 100%; background: #f5f5f5; margin-bottom: 8px; border-radius: 4px;"></div>
          <div style="height: 20px; width: 70%; background: #f5f5f5; margin-bottom: 8px; border-radius: 4px;"></div>
          <div style="height: 20px; width: 90%; background: #f5f5f5; border-radius: 4px;"></div>
        </div>
      `;

      document.body.appendChild(popover);

      // Get token data from context
      const tokenData = getToken(symbol);

      if (!tokenData) {
        popover.innerHTML = '<div style="color: #ff4d4f;">Token information not found</div>';
        return popover;
      }

      const priceChange = tokenData.price_change?.change_1D?.percentage || 0;
      const priceChangeColor = priceChange >= 0 ? "#16a34a" : "#dc2626";

      // Updated UI to match the screenshot
      popover.innerHTML = `
        <div class="token-popover-content">
          <!-- Header with token info -->
          <div style="display: flex; align-items: center; margin-bottom: 16px;">
            <img src="${tokenData.logo_uri}" alt="${
        tokenData.symbol
      }" style="width: 24px; height: 24px; border-radius: 50%;">
            <div style="margin-left: 12px; display: flex; flex-direction: row; gap: 4px; align-items: center;">
              <div style="font-weight: 700; font-size: 16px;">${tokenData.name}</div>
              <div style="font-size: 16px; opacity: 0.6; color: #6b7280;">${tokenData.symbol}</div>
            </div>
          </div>
          <hr style="margin: 12px 0; border: 0; border-top: 1px solid #f0f0f0;"/>
          
          <!-- Price and change -->
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="font-weight: 700; font-size: 20px;">$${
              tokenData.price?.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) || "N/A"
            }</div>
            <div style="font-weight: 600; font-size: 16px; color: ${priceChangeColor}; background-color: ${
        priceChange >= 0 ? "rgba(22, 163, 74, 0.1)" : "rgba(220, 38, 38, 0.1)"
      }; padding: 4px 12px; border-radius: 4px;">
              <span style="display: flex; align-items: center;">
                ${priceChange >= 0 ? "↑" : "↓"} ${Math.abs(priceChange).toFixed(2)}%
              </span>
            </div>
          </div>
          
          <!-- Stats -->
          <div style="border-top: 1px solid #f0f0f0; border-bottom: 1px solid #f0f0f0; padding: 12px 0; margin-bottom: 16px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
              <div style="color: #6b7280; font-size: 14px;">Market Cap:</div>
              <div style="font-weight: 600; font-size: 14px;">$${(tokenData.market_cap || 0).toLocaleString("en-US", {
                notation: "compact",
                maximumFractionDigits: 2,
              })}</div>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <div style="color: #6b7280; font-size: 14px;">Volume 24:</div>
              <div style="font-weight: 600; font-size: 14px;">$${(tokenData.volume || 0).toLocaleString("en-US", {
                notation: "compact",
                maximumFractionDigits: 1,
              })}</div>
            </div>
          </div>
          
          <!-- Action buttons -->
          <div style="display: flex; gap: 12px; justify-content: space-between;">
            <a target="_blank" href="https://dashboard.klarda.com/product/${tokenData.symbol}/overview" style="cursor: pointer !important; background-color: transparent; border: 1px solid #e5e7eb; border-radius: 50%; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.2931 7.37257V6.11254C11.2929 5.07847 10.935 4.0763 10.2801 3.27605C9.62515 2.47581 8.71359 1.92676 7.7 1.72204V0.700016C7.7 0.514361 7.62625 0.336309 7.49497 0.20503C7.3637 0.0737517 7.18565 0 7 0C6.81435 0 6.6363 0.0737517 6.50503 0.20503C6.37375 0.336309 6.3 0.514361 6.3 0.700016V1.72204C5.28641 1.92676 4.37485 2.47581 3.71994 3.27605C3.06503 4.0763 2.70712 5.07847 2.7069 6.11254V7.22277C2.70707 7.27216 2.71223 7.32142 2.7223 7.36977H2.7069C2.69631 7.95002 2.45855 8.50297 2.0447 8.90981C1.66896 9.27815 1.43984 9.77047 1.4 10.2951C1.4 10.6753 1.4 11.8303 2.4773 11.8303H4.0222C4.22509 12.4605 4.62269 13.0101 5.1578 13.4C5.69291 13.7899 6.33792 14 7 14C7.66208 14 8.30709 13.7899 8.8422 13.4C9.37731 13.0101 9.77491 12.4605 9.9778 11.8303H11.5227C12.6 11.8303 12.6 10.6753 12.6 10.2951C12.56 9.77072 12.3309 9.27867 11.9553 8.91051C11.542 8.50418 11.3042 7.95209 11.2931 7.37257ZM7 12.6003C6.71532 12.6 6.4351 12.5296 6.18408 12.3953C5.93307 12.261 5.71901 12.067 5.5608 11.8303H8.4392C8.28099 12.067 8.06693 12.261 7.81592 12.3953C7.5649 12.5296 7.28468 12.6 7 12.6003ZM11.2 10.4302H2.8C2.8 10.3833 2.8 10.335 2.8 10.2951C2.8 10.1992 2.975 9.98503 3.129 9.79673C3.7494 9.141 4.09939 8.27524 4.109 7.37257V6.11254C4.10767 5.32961 4.40678 4.576 4.94467 4.0071C5.48257 3.43819 6.21823 3.09736 7 3.05487C7.78214 3.09684 8.51833 3.43745 9.05666 4.0064C9.595 4.57536 9.8944 5.32926 9.8931 6.11254V7.22277C9.89327 7.27216 9.89843 7.32142 9.9085 7.36977H9.8931C9.90271 8.27244 10.2527 9.1382 10.8731 9.79393C11.0271 9.98223 11.2021 10.1964 11.2021 10.2923C11.2 10.335 11.2 10.3833 11.2 10.4302Z" fill="#1F2A37"/>
              <path d="M2.3051 2.52496C2.43712 2.65247 2.61394 2.72303 2.79748 2.72144C2.98102 2.71984 3.15659 2.64622 3.28637 2.51643C3.41616 2.38664 3.48978 2.21107 3.49137 2.02753C3.49297 1.84399 3.42241 1.66716 3.2949 1.53514L2.5949 0.83512C2.46288 0.707606 2.28606 0.637048 2.10252 0.638643C1.91898 0.640238 1.74341 0.713858 1.61363 0.843647C1.48384 0.973436 1.41022 1.14901 1.40863 1.33255C1.40703 1.51609 1.47759 1.69292 1.6051 1.82494L2.3051 2.52496Z" fill="#1F2A37"/>
              <path d="M2.1 4.83011C2.1 4.64446 2.02625 4.46641 1.89497 4.33513C1.7637 4.20385 1.58565 4.1301 1.4 4.1301H0.7C0.514348 4.1301 0.336301 4.20385 0.205025 4.33513C0.0737498 4.46641 0 4.64446 0 4.83011C0 5.01577 0.0737498 5.19382 0.205025 5.3251C0.336301 5.45638 0.514348 5.53013 0.7 5.53013H1.4C1.58565 5.53013 1.7637 5.45638 1.89497 5.3251C2.02625 5.19382 2.1 5.01577 2.1 4.83011Z" fill="#1F2A37"/>
              <path d="M11.2 2.73006C11.3856 2.73002 11.5637 2.65625 11.6949 2.52496L12.3949 1.82494C12.4618 1.76037 12.5151 1.68313 12.5518 1.59772C12.5885 1.51232 12.6078 1.42046 12.6086 1.32751C12.6094 1.23456 12.5917 1.14239 12.5565 1.05636C12.5213 0.970328 12.4693 0.89217 12.4036 0.826443C12.3379 0.760717 12.2597 0.708738 12.1737 0.673541C12.0876 0.638343 11.9955 0.620632 11.9025 0.621439C11.8096 0.622247 11.7177 0.641558 11.6323 0.678245C11.5469 0.714932 11.4697 0.768261 11.4051 0.83512L10.7051 1.53514C10.6072 1.63303 10.5406 1.75775 10.5136 1.89352C10.4866 2.02929 10.5005 2.17002 10.5534 2.29791C10.6064 2.42581 10.6961 2.53512 10.8112 2.61204C10.9263 2.68896 11.0616 2.73003 11.2 2.73006Z" fill="#1F2A37"/>
              <path d="M13.3 4.1301H12.6C12.4143 4.1301 12.2363 4.20385 12.105 4.33513C11.9737 4.46641 11.9 4.64446 11.9 4.83011C11.9 5.01577 11.9737 5.19382 12.105 5.3251C12.2363 5.45638 12.4143 5.53013 12.6 5.53013H13.3C13.4857 5.53013 13.6637 5.45638 13.795 5.3251C13.9263 5.19382 14 5.01577 14 4.83011C14 4.64446 13.9263 4.46641 13.795 4.33513C13.6637 4.20385 13.4857 4.1301 13.3 4.1301Z" fill="#1F2A37"/>
              </svg>
            </a>
            <button data-token-id="${tokenData._id || tokenData.id}" class="favorite-btn" style="cursor: pointer !important; background-color: transparent; border: 1px solid #e5e7eb; border-radius: 50%; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.33664 14C3.18038 13.9995 3.02609 13.9636 2.88449 13.8946C2.7429 13.8256 2.61741 13.7253 2.51675 13.6007C2.41608 13.476 2.34266 13.3299 2.30158 13.1727C2.26051 13.0154 2.25276 12.8507 2.27889 12.69L2.88302 9.01615L0.323702 6.41579C0.180374 6.26974 0.0790427 6.08481 0.0311502 5.88187C-0.0167423 5.67893 -0.00928814 5.46606 0.0526709 5.2673C0.11463 5.06854 0.228626 4.89181 0.381786 4.75707C0.534947 4.62233 0.72117 4.53493 0.91943 4.50476L4.45529 3.96877L6.03667 0.625029C6.12528 0.437437 6.26248 0.279469 6.43271 0.169007C6.60295 0.0585445 6.79943 0 6.99991 0C7.20039 0 7.39687 0.0585445 7.56711 0.169007C7.73734 0.279469 7.87454 0.437437 7.96316 0.625029L9.54453 3.96731L13.0804 4.5033C13.2787 4.53332 13.4649 4.62059 13.6181 4.75526C13.7713 4.88992 13.8854 5.06661 13.9473 5.26534C14.0093 5.46407 14.0167 5.67693 13.9688 5.87984C13.9209 6.08276 13.8195 6.26764 13.6761 6.4136L11.1168 9.01615L11.7209 12.6892C11.7548 12.8953 11.7327 13.1071 11.6572 13.3007C11.5817 13.4943 11.4558 13.6621 11.2937 13.7849C11.1315 13.9078 10.9397 13.9808 10.7398 13.9959C10.5399 14.0109 10.34 13.9673 10.1627 13.87L6.99991 12.1357L3.83716 13.87C3.68293 13.9551 3.51113 13.9998 3.33664 14ZM1.77487 5.84912L3.98277 8.09533C4.10759 8.22201 4.20094 8.37853 4.25473 8.55132C4.30853 8.7241 4.32114 8.90796 4.29148 9.08698L3.77066 12.2577L6.50079 10.7599C6.65489 10.6761 6.82611 10.6323 6.99991 10.6323C7.17371 10.6323 7.34493 10.6761 7.49903 10.7599L10.2292 12.2569L9.70763 9.08698C9.67822 8.90785 9.69108 8.72393 9.74512 8.55114C9.79915 8.37836 9.89273 8.22191 10.0177 8.09533L12.225 5.84985L9.17351 5.38762C9.00133 5.36147 8.83781 5.29212 8.69698 5.18554C8.55616 5.07895 8.44222 4.93831 8.36497 4.77568L6.99991 1.88833L5.63555 4.77276C5.55842 4.93562 5.44455 5.07653 5.30372 5.18337C5.16289 5.29021 4.9993 5.3598 4.82701 5.38616L1.77487 5.84912Z" fill="#1F2A37"/>
              </svg>
            </button>
            <a target="_blank" href="/${language}/markets/${symbol}" class="view-more-btn" style="cursor: pointer !important; height: 34px; flex: 1; background-color: #1A56DB; color: white; text-align: center; padding: 10px 12px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 12px; display: flex; align-items: center; justify-content: center;">
              View more
            </a>
          </div>
        </div>
      `;

      return popover;
    };

    // Set up the hover logic for a token link
    const setupTokenLink = (tokenLink: Element) => {
      // Skip if already enhanced
      if (tokenLink.getAttribute("data-popover-enhanced") === "true") return;

      // Mark as enhanced
      tokenLink.setAttribute("data-popover-enhanced", "true");

      // Get token symbol
      const symbol = tokenLink.getAttribute("data-token-symbol");
      if (!symbol) return;

      // Lazy load popover - only create it when needed
      let popover: HTMLElement | null = null;
      let isDestroyed = false;

      // Function to show popover
      const showPopover = () => {
        if (isDestroyed) return;

        // Create popover if it doesn't exist yet
        if (!popover) {
          popover = createPopover(symbol);
          
          // Setup event handlers for popover elements
          setupPopoverEvents(popover, symbol);
        }

        if (!popover) return;

        const rect = tokenLink.getBoundingClientRect();

        // Position the popover above the token link
        popover.style.top = `${window.scrollY + rect.top - popover.offsetHeight + 20}px`;
        popover.style.left = `${window.scrollX + rect.left + rect.width / 2}px`;
        popover.style.transform = "translateX(-50%)";

        // Show the popover
        popover.style.display = "block";
        
        setTimeout(() => {
          if (popover) {
            popover.style.opacity = "1";
          }
        }, 10);
      };

      // Setup event handlers for popover
      const setupPopoverEvents = (popoverElement: HTMLElement, tokenSymbol: string) => {
        // Add mouseleave event for popover
        popoverElement.addEventListener("mouseleave", () => {
          if (popoverElement && !tokenLink.matches(":hover")) {
            hidePopover();
          }
        });

        // Handle favorite button clicks
        const favoriteButtons = popoverElement.querySelectorAll('.favorite-btn');
        favoriteButtons.forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const tokenId = (btn as HTMLElement).getAttribute('data-token-id');
            if (tokenId) {
              handleAddTokenToWatchlist({ _id: tokenId });
              
              // Important: Delay hiding to allow the click to register
              setTimeout(() => {
                destroyPopover();
              }, 100);
            }
          });
        });

        // Handle view more button clicks
        const viewMoreButtons = popoverElement.querySelectorAll('.view-more-btn');
        viewMoreButtons.forEach(btn => {
          btn.addEventListener('click', () => {
            // Wait for the click to register before destroying
            setTimeout(() => {
              destroyPopover();
            }, 100);
          });
        });

        // Handle all other link clicks
        const allLinks = popoverElement.querySelectorAll('a:not(.view-more-btn)');
        allLinks.forEach(link => {
          link.addEventListener('click', () => {
            // Wait for the click to register before destroying
            setTimeout(() => {
              destroyPopover();
            }, 100);
          });
        });
      };

      // Hide popover
      const hidePopover = () => {
        if (!popover) return;
        
        popover.style.opacity = "0";
        setTimeout(() => {
          if (popover && !popover.matches(":hover") && !tokenLink.matches(":hover")) {
            popover.style.display = "none";
          }
        }, 200);
      };

      // Completely destroy the popover (important for resetting state)
      const destroyPopover = () => {
        if (!popover) return;
        
        hidePopover();
        
        // Set a timeout to ensure animation completes before removing
        setTimeout(() => {
          if (popover) {
            popover.remove();
            popover = null;
            isDestroyed = true;
            
            // Reset isDestroyed after a brief period to allow re-creation
            setTimeout(() => {
              isDestroyed = false;
            }, 300);
          }
        }, 300);
      };

      // Show popover on hover
      tokenLink.addEventListener("mouseenter", showPopover);

      // Hide popover when mouse leaves the token link
      tokenLink.addEventListener("mouseleave", () => {
        if (popover && !popover.matches(":hover")) {
          hidePopover();
        }
      });

      // Prevent default link behavior and show popover on click
      tokenLink.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        showPopover();
      });
      
      // Add pointer cursor to token links
      tokenLink.setAttribute("style", "cursor: pointer !important;");
    };

    // Find and enhance all token links
    const enhanceTokenLinks = () => {
      const tokenLinks = document.querySelectorAll('[data-lexical-token-link="true"]');
      tokenLinks.forEach(setupTokenLink);
    };

    // Initial enhancement after content loads
    setTimeout(enhanceTokenLinks, 500);

    // Observe DOM changes to catch new token links
    const observer = new MutationObserver(mutations => {
      let hasNewContent = false;

      for (const mutation of mutations) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          hasNewContent = true;
          break;
        }
      }

      if (hasNewContent) {
        setTimeout(enhanceTokenLinks, 100);
      }
    });

    // Start observing both possible content containers
    const blogContent = document.getElementById("blog-detail");
    const lexicalContent = document.getElementById("lexical-blog-detail");

    if (blogContent) {
      observer.observe(blogContent, { childList: true, subtree: true });
    }

    if (lexicalContent) {
      observer.observe(lexicalContent, { childList: true, subtree: true });
    }

    // Cleanup on unmount
    return () => {
      observer.disconnect();
      document.querySelectorAll(".token-popover").forEach(popover => {
        popover.remove();
      });
    };
  }, [language, getToken, handleAddTokenToWatchlist]);

  return null; // This component doesn't render anything
};